import {
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import type { TagRuleEditFormType } from "@/pages/tina/tag-rules/schemas/tag-rule.form";
import type { TagRuleRead } from "@/types/tina";

const RESOURCE = "tina-tag-rules";

// The orchestrator forbids extra fields (extra="forbid"). Only the editable
// columns may be sent on POST/PATCH — never id/created_*/updated_* (id rides
// in the URL). `updated_by` is left to a follow-up (curator identity).
const toPayload = (values: TagRuleEditFormType) => ({
  tag_name: values.tag_name,
  explanation: values.explanation,
  enabled: values.enabled,
});

export const useTagRulesRepository = () => {
  const { getOne } = useDataProvider()("tina");
  const { mutateAsync: createAsync } = useCreate({
    resource: RESOURCE,
    dataProviderName: "tina",
  });
  const { mutateAsync: updateAsync } = useUpdate({
    resource: RESOURCE,
    dataProviderName: "tina",
  });
  const { open } = useNotification();
  const { edit } = useNavigation();

  async function get(id: string): Promise<TagRuleRead> {
    const response = await getOne({ resource: RESOURCE, id });
    return response as unknown as TagRuleRead;
  }

  async function create(values: TagRuleEditFormType): Promise<string> {
    try {
      const response = await createAsync({
        resource: RESOURCE,
        dataProviderName: "tina",
        values: toPayload(values),
      });
      const id = (response.data as TagRuleRead).id;
      open?.({
        type: "success",
        message: "Tag rule created",
        description: `Created rule for "${values.tag_name}"`,
      });
      edit(RESOURCE, id, "replace");
      return id;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to create tag rule",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  async function update(values: TagRuleEditFormType): Promise<string> {
    const id = values.id;
    if (!id) {
      throw new Error("Cannot update a tag rule without an id");
    }
    try {
      const response = await updateAsync({
        resource: RESOURCE,
        dataProviderName: "tina",
        id,
        values: toPayload(values),
      });
      open?.({
        type: "success",
        message: "Tag rule updated",
        description: `Updated rule for "${values.tag_name}"`,
      });
      return (response.data as TagRuleRead).id ?? id;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to update tag rule",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  return { get, create, update };
};
